// CROS API Client — connects to Node.js backend on EC2
// Pure JWT auth, no third-party auth providers

const API_URL = import.meta.env.VITE_API_URL as string || 'https://api.creatorrepos.com';

function getStoredToken(): string | null {
  return localStorage.getItem('cros_token');
}

export function setStoredToken(token: string): void {
  localStorage.setItem('cros_token', token);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem('cros_refresh_token', token);
}

export function clearTokens(): void {
  localStorage.removeItem('cros_token');
  localStorage.removeItem('cros_refresh_token');
  // PII: session-only SSN cache used by the W-9 send flow. Scrubbed on logout
  // to guarantee no PII survives the session. See TAX_FEATURE_SPEC.md.
  sessionStorage.removeItem('cros:w9:ssn');
}

export function getAuthToken(): string | null {
  return getStoredToken();
}

export function isAuthenticated(): boolean {
  return !!getStoredToken();
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getStoredToken();

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshToken = localStorage.getItem('cros_refresh_token');
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResponse.ok) {
          const { token: newToken, refresh_token: newRefresh } = await refreshResponse.json();
          setStoredToken(newToken);
          if (newRefresh) setRefreshToken(newRefresh);

          const retryResponse = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${newToken}`,
              ...options.headers,
            },
          });

          if (!retryResponse.ok) {
            const error = await retryResponse.json().catch(() => ({ message: 'Request failed' }));
            throw new Error(error.message || `HTTP ${retryResponse.status}`);
          }

          return retryResponse.json();
        }
      } catch {
        clearTokens();
        window.location.href = '/login';
      }
    }

    clearTokens();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    const details = error.details ? ': ' + Object.entries(error.details).map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`).join('; ') : '';
    throw new Error((error.message || error.error || `HTTP ${response.status}`) + details);
  }

  return response.json();
}

// Custom error type for auth flow errors. Preserves structured
// fields from the backend response — currently `deleted: true`
// for soft-deleted-account login responses (R32-3), so Login.tsx
// can render the reactivate CTA without string-matching messages.
export class AuthError extends Error {
  readonly deleted: boolean;
  readonly status: number;
  constructor(message: string, status: number, deleted = false) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
    this.deleted = deleted;
  }
}

// Auth functions
export const auth = {
  async signUp(email: string, password: string, fullName: string) {
    const data = await apiRequest<{ token: string; refresh_token: string; user: Record<string, unknown> }>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName }),
    });
    setStoredToken(data.token);
    setRefreshToken(data.refresh_token);
    return data;
  },

  async signIn(email: string, password: string) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body.message || body.error || `HTTP ${response.status}`;
      // Backend (R32-3) attaches deleted: true on soft-deleted-account
      // 401 responses. Preserving the flag through the thrown error
      // lets Login.tsx render the reactivate CTA without string-
      // matching messages. Direct fetch (not apiRequest) avoids the
      // 401 force-redirect-to-/login behavior that's intended for
      // authed-request token expiration, not login rejections.
      throw new AuthError(message, response.status, body.deleted === true);
    }

    const data = await response.json() as {
      token?: string;
      refresh_token?: string;
      user?: Record<string, unknown>;
      requires_2fa?: boolean;
      pre_auth_token?: string;
    };
    // 2FA-enabled accounts get a pre-auth token instead of real tokens; the
    // caller must complete login via verify2faLogin. Do NOT store anything yet.
    if (data.requires_2fa && data.pre_auth_token) {
      return { requires_2fa: true as const, pre_auth_token: data.pre_auth_token };
    }
    setStoredToken(data.token as string);
    setRefreshToken(data.refresh_token as string);
    return {
      token: data.token as string,
      refresh_token: data.refresh_token as string,
      user: data.user as Record<string, unknown>,
    };
  },

  // Second factor of login: exchange the pre-auth token + a TOTP or backup code
  // for real tokens. Stored exactly like a normal login on success.
  async verify2faLogin(preAuthToken: string, code: string) {
    const response = await fetch(`${API_URL}/api/auth/2fa/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pre_auth_token: preAuthToken, code }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body.message || body.error || `HTTP ${response.status}`;
      throw new AuthError(message, response.status);
    }
    const data = await response.json() as {
      token: string;
      refresh_token: string;
      user: Record<string, unknown>;
    };
    setStoredToken(data.token);
    setRefreshToken(data.refresh_token);
    return data;
  },

  async signOut() {
    clearTokens();
  },

  async resetPassword(email: string) {
    return apiRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async confirmPasswordReset(token: string, newPassword: string) {
    return apiRequest<{ success: boolean }>('/api/auth/reset-password/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    return apiRequest<{ success: boolean }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  // ─── 2FA (TOTP) enrollment (CP1 endpoints) ──────────────────────────────────
  // All authed via apiRequest (Bearer token). setup returns the QR + manual
  // secret; confirm activates 2FA and returns the one-time backup codes.
  async setup2FA(): Promise<{ otpauth_uri: string; qr_data_url: string; secret: string }> {
    return apiRequest<{ otpauth_uri: string; qr_data_url: string; secret: string }>('/api/auth/2fa/setup', {
      method: 'POST',
    });
  },

  async confirm2FA(code: string): Promise<{ success: boolean; backup_codes: string[] }> {
    return apiRequest<{ success: boolean; backup_codes: string[] }>('/api/auth/2fa/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async regenerateBackupCodes(password: string): Promise<{ backup_codes: string[] }> {
    return apiRequest<{ backup_codes: string[] }>('/api/auth/2fa/backup-codes/regenerate', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  async requestReactivation(email: string) {
    return apiRequest<{ message: string }>('/api/auth/reactivate-request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async confirmReactivation(token: string, newPassword: string) {
    const response = await fetch(`${API_URL}/api/auth/reactivate-confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body.message || body.error || `HTTP ${response.status}`;
      throw new Error(message);
    }

    const data = await response.json() as {
      success: boolean;
      token: string;
      refresh_token: string;
      user: Record<string, unknown>;
    };
    // Auto-login after successful reactivation — backend response
    // mirrors signIn shape for exactly this purpose.
    setStoredToken(data.token);
    setRefreshToken(data.refresh_token);
    return data;
  },

  async verifyEmail(token: string) {
    return apiRequest<{ success: boolean; email: string }>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  async resendVerification() {
    return apiRequest<{ success: boolean; already_verified?: boolean }>('/api/auth/resend-verification', {
      method: 'POST',
    });
  },
};
